import { useCallback, useEffect, useRef, useState } from 'react'
import type { Navigate } from '../App'
import type { Block, Island } from '../types'
import { useDrillableIslands, useSettings } from '../state/store'
import { useRecorder } from '../state/useRecorder'
import type { GradeResult } from '../lib/grade'
import { gradeFor, inputClass, languageOf, resolveMode } from '../lib/languages'
import { readiness, scoreClass } from '../lib/analysis'
import { isDue } from '../lib/srs'
import { Countdown, useCountdown } from '../components/Countdown'
import { GradeReport } from '../components/GradeReport'
import { DrillExit } from '../components/DrillExit'

type Phase = 'ready' | 'live' | 'review'

interface Target {
  island: Island
  block: Block
}

/**
 * A random block, cold, on a short clock.
 *
 * Everything else in the app tells you what is coming. Real conversation does
 * not, and an island you can only reach after being told which island it is has
 * not actually been learned. Selection is weighted toward what is due and what
 * you are worst at, so the pool sharpens itself over time.
 */
/**
 * Whether a block can be fired at someone.
 *
 * It needs text to produce and an English prompt to produce it from. It needs a
 * phonetic line ONLY in a language that has one — this used to demand both
 * unconditionally, which is true of Mandarin and of nothing else. The other
 * four store an empty phonetic field by design, so the pool came back empty and
 * the drill was silently dead on four sites out of five.
 */
function drillable(block: Block, hasPhonetics: boolean): boolean {
  if (!block.hanzi.trim() || !block.english.trim()) return false
  return hasPhonetics ? Boolean(block.pinyin.trim()) : true
}

function pickTarget(islands: Island[], avoidId: string | null): Target | null {
  const usable = islands.filter((i) =>
    i.blocks.some((b) => drillable(b, languageOf(i.language).hasPhonetics)),
  )
  if (usable.length === 0) return null

  const weighted = usable.flatMap((island) => {
    const state = readiness(island)
    let weight = 1
    if (isDue(island.srs)) weight += 2
    if (state === 'cold' || state === 'warming') weight += 2
    if (island.stats.lastScore !== null && island.stats.lastScore < 70) weight += 2
    // Don't fire the same island twice running unless it's the only one.
    if (island.id === avoidId && usable.length > 1) weight = 0.2
    return Array.from({ length: Math.max(1, Math.round(weight * 2)) }, () => island)
  })

  const island = weighted[Math.floor(Math.random() * weighted.length)]
  const candidates = island.blocks.filter((b) =>
    drillable(b, languageOf(island.language).hasPhonetics),
  )
  const block = candidates[Math.floor(Math.random() * candidates.length)]
  return { island, block }
}

export function AmbushDrill({
  navigate,
  setFocused,
}: {
  navigate: Navigate
  setFocused: (v: boolean) => void
}) {
  // An ambush is the app choosing for you, so it chooses from what you can say.
  const islands = useDrillableIslands()
  const settings = useSettings()
  const record = useRecorder()

  const [phase, setPhase] = useState<Phase>('ready')
  const [target, setTarget] = useState<Target | null>(null)
  const [attempt, setAttempt] = useState('')
  const [result, setResult] = useState<GradeResult | null>(null)
  const [nothingToFire, setNothingToFire] = useState(false)
  const [run, setRun] = useState<number[]>([])
  const startedAt = useRef(Date.now())
  const attemptRef = useRef('')
  attemptRef.current = attempt
  const targetRef = useRef<Target | null>(null)
  targetRef.current = target

  const mode = settings.inputMode
  const limit = settings.ambushSeconds

  const submit = useCallback(() => {
    const current = targetRef.current
    if (!current) return
    const lang = languageOf(current.island.language)
    const graded = gradeFor(
      lang.code,
      current.block,
      attemptRef.current,
      resolveMode(lang.code, mode),
      settings.requireTones,
    )
    setResult(graded)
    setPhase('review')
    setRun((prev) => [...prev, graded.score])
    record({
      islandId: current.island.id,
      mode: 'ambush',
      score: graded.score,
      seconds: (Date.now() - startedAt.current) / 1000,
      inputMode: mode,
      result: graded,
    })
  }, [mode, settings.requireTones, record])

  const remaining = useCountdown(limit, phase === 'live', submit)

  useEffect(() => {
    setFocused(phase === 'live')
  }, [phase, setFocused])

  useEffect(() => () => setFocused(false), [setFocused])

  const fire = () => {
    const next = pickTarget(islands, target?.island.id ?? null)
    if (!next) {
      // Never return silently. This is how the language bug hid: the button
      // looked ready, did nothing, and said nothing.
      setNothingToFire(true)
      return
    }
    setNothingToFire(false)
    setTarget(next)
    setAttempt('')
    setResult(null)
    startedAt.current = Date.now()
    setPhase('live')
  }

  const averageRun = run.length ? Math.round(run.reduce((a, b) => a + b, 0) / run.length) : null

  if (islands.length === 0) {
    return (
      <div className="empty">
        Ambush needs islands to draw from.{' '}
        <button type="button" className="btn is-small" onClick={() => navigate({ view: 'islands' })}>
          Build one
        </button>
      </div>
    )
  }

  return (
    <>
      {/* Present in every phase, including mid-countdown, where the rail is
          hidden and this is the only way out. */}
      <DrillExit
        onLeave={() => navigate({ view: 'dashboard' })}
        hint={phase === 'live' ? 'This one will not be scored.' : undefined}
      />

      <header className="page-head">
        <div className="eyebrow">
          Ambush{averageRun !== null && ` · ${run.length} fired · averaging ${averageRun}`}
        </div>
        <h1 className="page-title">
          <span className="page-title-zh">{languageOf(settings.activeLanguage).ui.ambush}</span> No warning
        </h1>
        {nothingToFire && (
          <p className="notice is-error" style={{ marginTop: '0.8rem' }}>
            None of your {languageOf(settings.activeLanguage).name} islands have a block ready to
            fire — a block needs the phrase itself and its English meaning
            {languageOf(settings.activeLanguage).hasPhonetics ? ' and its pinyin' : ''}. Fill one in
            from the Islands screen and come back.
          </p>
        )}

        {phase === 'ready' && (
          <p className="lede">
            A block you did not choose, with {limit} seconds on it. You will not be told which
            island it came from until you have answered — that recognition is the thing being
            drilled.
          </p>
        )}
      </header>

      {phase === 'ready' && (
        <div className="card">
          <button type="button" className="btn is-live" onClick={fire}>
            Fire one at me
          </button>
        </div>
      )}

      {phase !== 'ready' && target && (
        <>
          <div className={`stage ${phase === 'live' ? 'is-live' : ''}`}>
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <div className="stage-cue">Say it in {languageOf(target.island.language).name} — now</div>
              {phase === 'live' && <Countdown remaining={remaining} total={limit} />}
            </div>

            <p style={{ fontSize: '1.45rem', margin: '0.4rem 0 0' }}>{target.block.english}</p>

            {phase === 'review' && (
              <p className="muted appear" style={{ fontSize: '0.86rem', marginBottom: 0 }}>
                from <span className="hanzi">{target.island.titleZh}</span> · {target.island.title}
              </p>
            )}
          </div>

          <div className="card">
            <input
              className={`answer-input ${inputClass(mode)}`}
              value={attempt}
              autoFocus
              lang={mode === 'hanzi' ? 'zh-CN' : 'en'}
              placeholder={mode === 'hanzi' ? '快！' : 'go…'}
              onChange={(e) => setAttempt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
                e.preventDefault()
                if (phase === 'live') submit()
                else fire()
              }}
              disabled={phase !== 'live'}
            />

            <div className="row" style={{ marginTop: '0.8rem' }}>
              {phase === 'live' ? (
                <button type="button" className="btn is-live" onClick={submit}>
                  Lock it in
                </button>
              ) : (
                <>
                  <button type="button" className="btn is-primary" onClick={fire}>
                    Again
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => navigate({ view: 'island', id: target.island.id })}
                  >
                    Open {target.island.title}
                  </button>
                </>
              )}
              {result && (
                <span
                  className={`score ${scoreClass(result.score)}`}
                  style={{ fontSize: '1.4rem', marginLeft: 'auto' }}
                >
                  {result.score}
                </span>
              )}
            </div>
          </div>

          {phase === 'review' && result && (
            <GradeReport
              result={result}
              mode={mode}
              target={target.block}
              language={languageOf(target.island.language).code}
            />
          )}
        </>
      )}
    </>
  )
}
